import './loader-assets/assets.css';
import './streamlain-brand-loader.css';

import {
  isStreamLainMotionEnabled,
  streamLainAnimationController,
  subscribeStreamLainMotionPolicy,
} from '@blocksuite/affine/shared/utils';
import { BRAND_DISPLAY_NAME } from '@streamlain/brand';
import { createElement, type CSSProperties } from 'react';

import * as styles from './brand.css';
import sprite from './loader-assets/manifest.json';
import { createStreamLainBrandLoaderPlayer } from './streamlain-brand-loader-runtime';
import { STREAMLAIN_CORE_LOGO } from './streamlain-logo-mark';

/** The same element is usable from React and BlockSuite/Lit without a React dependency there. */
export function registerStreamLainBrandLoader() {
  if (
    typeof window === 'undefined' ||
    customElements.get('streamlain-brand-loader')
  )
    return;
  class BrandLoaderElement extends HTMLElement {
    static observedAttributes = ['animated'];
    private player?: ReturnType<typeof createStreamLainBrandLoaderPlayer>;
    private unsubscribe?: () => void;
    connectedCallback() {
      if (this.player) return;
      this.classList.add(styles.mark, 'brand-logo');
      this.setAttribute('data-streamlain-brand-loader', 'true');
      if (!this.hasAttribute('role')) this.setAttribute('role', 'img');
      if (!this.hasAttribute('aria-label'))
        this.setAttribute('aria-label', BRAND_DISPLAY_NAME);
      if (!this.querySelector('[data-streamlain-loader-sheet]')) {
        const ns = 'http://www.w3.org/2000/svg';
        const svg = document.createElementNS(ns, 'svg');
        svg.setAttribute('viewBox', STREAMLAIN_CORE_LOGO.mark.viewBox);
        svg.setAttribute('data-streamlain-loader-fallback', 'true');
        svg.setAttribute('aria-hidden', 'true');
        svg.setAttribute('focusable', 'false');
        for (const layer of ['upper', 'lower'] as const) {
          const group = document.createElementNS(ns, 'g');
          group.setAttribute(
            'transform',
            STREAMLAIN_CORE_LOGO.mark[layer].transform
          );
          const path = document.createElementNS(ns, 'path');
          path.setAttribute('d', STREAMLAIN_CORE_LOGO.mark[layer].path);
          path.setAttribute(
            'fill',
            `var(--sl-brand-mark-${layer}, ${layer === 'upper' ? STREAMLAIN_CORE_LOGO.colors.primary : STREAMLAIN_CORE_LOGO.colors.secondary})`
          );
          group.append(path);
          svg.append(group);
        }
        const sheet = document.createElement('span');
        sheet.setAttribute('data-streamlain-loader-sheet', 'true');
        sheet.setAttribute('aria-hidden', 'true');
        this.append(svg, sheet);
      }
      this.player = createStreamLainBrandLoaderPlayer(
        this,
        sprite,
        streamLainAnimationController
      );
      this.unsubscribe = subscribeStreamLainMotionPolicy(() =>
        this.updateMotion()
      );
      this.updateMotion();
    }
    private updateMotion() {
      const animated =
        this.getAttribute('animated') !== 'false' &&
        isStreamLainMotionEnabled();
      if (animated) this.setAttribute('data-flow-animation', 'true');
      else this.removeAttribute('data-flow-animation');
      this.player?.setEnabled(animated);
    }
    attributeChangedCallback() {
      this.updateMotion();
    }
    disconnectedCallback() {
      this.unsubscribe?.();
      this.unsubscribe = undefined;
      this.player?.dispose();
      this.player = undefined;
    }
  }
  customElements.define('streamlain-brand-loader', BrandLoaderElement);
}

// Register before React/Lit mount hosts; safe when rendered on the server.
registerStreamLainBrandLoader();

export function BrandLoader({
  size = 160,
  animated = true,
  className,
  style,
  'aria-label': label = BRAND_DISPLAY_NAME,
}: {
  size?: number;
  animated?: boolean;
  className?: string;
  style?: CSSProperties;
  'aria-label'?: string;
}) {
  return createElement('streamlain-brand-loader', {
    role: 'img',
    'aria-label': label,
    animated: String(animated),
    className: `${styles.mark} brand-logo ${className ?? ''}`.trim(),
    style: { '--sl-brand-loader-size': `${size}px`, ...style } as CSSProperties,
  });
}
